import { Kind, type Msg } from "@interlucent/organization-protocol";
import { getWs } from "../wsConnection";

const DEFAULT_TIMEOUT_MS = 15_000;

export interface WaitForAckOptions {
  /** AbortSignal for cancellation (e.g., on timeout) */
  signal?: AbortSignal;
  /** Timeout in milliseconds. Defaults to 15 000ms. Pass 0 to disable. */
  timeoutMs?: number;
}

export function waitForAck<T = unknown>(
  corr: string,
  options?: WaitForAckOptions
): Promise<T> {
  const ws = getWs();
  if (!ws) {
    return Promise.reject(new Error("No WebSocket connection"));
  }

  return new Promise((resolve, reject) => {
    const timeoutMs = options?.timeoutMs ?? DEFAULT_TIMEOUT_MS;
    let timer: ReturnType<typeof setTimeout> | null = null;

    const cleanup = () => {
      ws.removeEventListener("message", onMsg);
      if (timer) {
        clearTimeout(timer);
        timer = null;
      }
    };

    const onMsg = (ev: MessageEvent) => {
      try {
        const m: Msg = JSON.parse(ev.data);
        if (m.corr !== corr) {
          return;
        }
        if (m.kind === Kind.Err) {
          cleanup();
          reject(new Error((m.payload as any)?.message ?? "Unexpected error"));
        } else if (m.kind === Kind.Ack) {
          cleanup();
          resolve(m.payload as T);
        }
      } catch {
        // ignore parse errors and keep waiting
      }
    };

    // Handle abort signal for proper cleanup on timeout/cancellation
    if (options?.signal) {
      if (options.signal.aborted) {
        reject(new DOMException("Aborted", "AbortError"));
        return;
      }
      options.signal.addEventListener("abort", () => {
        cleanup();
        reject(new DOMException("Aborted", "AbortError"));
      }, { once: true });
    }

    // Built-in timeout to prevent permanently hung promises
    if (timeoutMs > 0) {
      timer = setTimeout(() => {
        cleanup();
        reject(new DOMException("waitForAck timed out", "TimeoutError"));
      }, timeoutMs);
    }

    ws.addEventListener("message", onMsg);
  });
}
